// @ts-check

/**
 * @file Renders the work status index route.
 */

import _ from "gettext-universal/build/src/translate.js"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"
import {useRouter} from "expo-router"
import {memo, useEffect} from "react"
import {shapeComponent, ShapeComponent} from "set-state-compare/build/shape-component.js"
import {ScrollView, View} from "react-native-propforge"
import Card from "../src/components/card"
import errorMessage from "../src/shared/error-message"
import LabeledTextInput from "../src/components/labeled-text-input"
import LoadingState from "../src/components/loading-state"
import PageHeader from "../src/components/page-header"
import ProjectsSelect from "../src/components/projects/select"
import {Timelog} from "../src/frontend-models/timelog"
import UsersSelect from "../src/components/users/select"
import {pushQueryParamUpdates} from "on-location-changed/build/query-param-updates"
import useCurrentQueryParams from "on-location-changed/build/use-current-query-params"
import useSelectedQueryParams from "on-location-changed/build/use-selected-query-params"
import {identifierListParam, optionalRouteParam} from "../src/shared/route-params"
import useThemeColors, {useThemeName} from "../src/shared/use-theme-colors"
import Text from "@kaspernj/api-maker/build/utils/text"
import ScreenTitle from "../src/components/screen-title"

/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @import {WorkstatusDaySummary, WorkstatusSummary} from "../../shared/timelog-dashboard-types.js" */
/** @typedef {Record<string, string | string[] | undefined>} RouteQueryParams */
/** @type {Record<string, object>} */
const styles = {}
const WORKSTATUS_QUERY_PARAM_SELECTOR = ["date_from", "date_to", "project_ids", "user_ids"]

/**
 * Formats seconds as decimal hours.
 * @param {number} seconds Duration in seconds.
 * @returns {string} Decimal hour label.
 */
function formatHours(seconds) {
  return (seconds / 3600).toFixed(1)
}

/**
 * Formats a money amount without cents, matching the old workstatus screen.
 * @param {number} amount Amount in major currency units.
 * @param {string | null} currency Currency code.
 * @returns {string} Money label.
 */
function formatMoneyAmount(amount, currency) {
  const formattedAmount = new Intl.NumberFormat(undefined, {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0
  }).format(Number(amount) || 0)

  return currency ? `${formattedAmount} ${currency}` : formattedAmount
}

/**
 * Builds the first day of the current local month.
 * @returns {string} Date string in YYYY-MM-DD format.
 */
function firstOfMonth() {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-01`
}

/**
 * Builds the last day of the current local month.
 * @returns {string} Date string in YYYY-MM-DD format.
 */
function endOfMonth() {
  const now = new Date()
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0)
  return `${lastDay.getFullYear()}-${String(lastDay.getMonth() + 1).padStart(2, "0")}-${String(lastDay.getDate()).padStart(2, "0")}`
}

/**
 * Builds today's local date.
 * @returns {string} Date string in YYYY-MM-DD format.
 */
function todayIsoDate() {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`
}

/**
 * Formats an ISO-like date string with the runtime locale.
 * @param {string} dateString Date string to format.
 * @returns {string} Localized date label.
 */
function formatDateLocal(dateString) {
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return new Date(`${dateString}T00:00:00`).toLocaleDateString()
  }
  return new Date(dateString).toLocaleDateString()
}

/**
 * Builds a chronological list of YYYY-MM-DD strings between two
 * inclusive date strings, capping at 366 entries so a typo in the date
 * picker can never blow up the render.
 * @param {string} fromIso Range start in YYYY-MM-DD.
 * @param {string} toIso Range end in YYYY-MM-DD.
 * @returns {string[]} ISO dates from `fromIso` through `toIso` inclusive.
 */
function enumerateDates(fromIso, toIso) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(fromIso) || !/^\d{4}-\d{2}-\d{2}$/.test(toIso)) return []
  const start = new Date(`${fromIso}T00:00:00`)
  const end = new Date(`${toIso}T00:00:00`)
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) return []
  /** @type {string[]} */
  const dates = []
  const cursor = new Date(start)
  while (cursor <= end && dates.length < 366) {
    dates.push(`${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, "0")}-${String(cursor.getDate()).padStart(2, "0")}`)
    cursor.setDate(cursor.getDate() + 1)
  }
  return dates
}

/** @typedef {{[key: string]: unknown}} WorkstatusIndexScreenProps */

/**
 * @typedef {object} WorkstatusIndexScreenState
 * @property {string} dateFrom Start date filter.
 * @property {string} dateTo End date filter.
 * @property {boolean} loading Whether summary data is loading.
 * @property {string[]} projectIds Selected project identifiers.
 * @property {{kind: "error", text: string} | null} status Error status shown above the report.
 * @property {WorkstatusSummary | null} summary Loaded work status summary.
 * @property {string[]} userIds Selected user identifiers.
 */

/**
 * Renders work status filters, summary totals, and daily rows.
 * @augments {ShapeComponent<WorkstatusIndexScreenProps, WorkstatusIndexScreenState>}
 */
class WorkstatusIndexScreen extends ShapeComponent {
  /** @type {() => RouteQueryParams} */
  currentQueryParams
  /** @type {RouteQueryParams} */
  params
  /** @type {ReturnType<typeof useRouter>} */
  router
  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()
  /** @type {ReturnType<typeof setTimeout> | null} */
  dateFromDebounceTimer = null
  /** @type {ReturnType<typeof setTimeout> | null} */
  dateToDebounceTimer = null
  loadRequestId = 0
  /** @type {WorkstatusIndexScreenState} */
  state = {
    dateFrom: "",
    dateTo: "",
    loading: true,
    projectIds: /** @type {string[]} */ [],
    status: /** @type {{kind: "error", text: string} | null} */ null,
    summary: /** @type {WorkstatusSummary | null} */ null,
    userIds: /** @type {string[]} */ []
  }

  /**
   * Initializes translations, route filters, and loading effects.
   * @returns {void} Nothing.
   */
  setup() {
    useLocale()
    const params = /** @type {RouteQueryParams} */ (useSelectedQueryParams(WORKSTATUS_QUERY_PARAM_SELECTOR))
    const currentQueryParams = /** @type {() => RouteQueryParams} */ (useCurrentQueryParams())
    const router = useRouter()

    const initialDateFrom = optionalRouteParam(params, "date_from", "Date from") ?? firstOfMonth()
    const initialDateTo = optionalRouteParam(params, "date_to", "Date to") ?? endOfMonth()
    const initialUserIds = identifierListParam(params.user_ids, "User IDs")
    const initialProjectIds = identifierListParam(params.project_ids, "Project IDs")
    this.currentQueryParams = currentQueryParams
    this.params = params
    this.router = router
    this.colors = useThemeColors()
    this.themeName = useThemeName()
    this.state.dateFrom = initialDateFrom
    this.state.dateTo = initialDateTo
    this.state.projectIds = initialProjectIds
    this.state.userIds = initialUserIds
    const dateFrom = this.s.dateFrom
    const dateTo = this.s.dateTo
    const userIdsKey = this.s.userIds.join(",")
    const projectIdsKey = this.s.projectIds.join(",")
    useEffect(() => {
      void this.loadData()
    }, [dateFrom, dateTo, userIdsKey, projectIdsKey])
    useEffect(() => {
      return () => {
        if (this.dateFromDebounceTimer) clearTimeout(this.dateFromDebounceTimer)
        if (this.dateToDebounceTimer) clearTimeout(this.dateToDebounceTimer)
      }
    }, [])
  }

  /**
   * Renders the work status index route.
   * @returns {ReactNode} Work status index content.
   */
  render() {
    const {loading, status} = this.s
    return (
      <View
        style={styles.screen ||= {
          flex: 1
        }}
        testID="workstatusIndexScreen"
      >
        <ScreenTitle>{_("Work status")}</ScreenTitle>
        <View
          testID="workstatusIndexScreen/content"
        >
          <PageHeader
            subtitle={_("Daily timelogs aggregated for the selected range")}
            testID="workstatusIndexScreen/header"
            title={_("Work status")}
          />
          {this.renderFilters()}
          {status &&
            <Text
              style={styles[`statusError-${this.tt.themeName}`] ||= {
                color: this.tt.colors.dangerText,
                fontWeight: 600,
                marginBottom: 16
              }}
              testID="workstatusIndexScreen/status"
            >
              {status.text}
            </Text>
          }
          {loading &&
            <LoadingState testID="workstatusIndexScreen/loadingText" />
          }
          {!loading && this.s.summary && this.renderSummary()}
          {!loading && this.s.summary && this.renderDays()}
        </View>
      </View>
    )
  }

  /**
   * Renders date and relationship filters.
   * @returns {ReactNode} Filter controls.
   */
  renderFilters() {
    const {
      dateFrom,
      dateTo,
      projectIds,
      userIds
    } = this.s
    return (
      <Card
        style={styles.filtersCardSpacing ||= {gap: 12, marginBottom: 16}}
        testID="workstatusIndexScreen/filters"
      >
        <View
          style={styles.filterRow ||= {
            flexDirection: "row",
            flexWrap: "wrap",
            gap: 12
          }}
          testID="workstatusIndexScreen/filterRow"
        >
          <LabeledTextInput
            defaultValue={dateFrom}
            label={_("Date from")}
            onChangeText={this.tt.onDateFromChange}
            testID="workstatusIndexScreen/dateFromInput"
          />
          <LabeledTextInput
            defaultValue={dateTo}
            label={_("Date to")}
            onChangeText={this.tt.onDateToChange}
            testID="workstatusIndexScreen/dateToInput"
          />
        </View>
        <View
          style={styles.filterRow ||= {
            flexDirection: "row",
            flexWrap: "wrap",
            gap: 12
          }}
          testID="workstatusIndexScreen/multiSelectRow"
        >
          <View
            style={styles.filterSelect ||= {
              flex: 1,
              minWidth: 220
            }}
            testID="workstatusIndexScreen/userFilterField"
          >
            <UsersSelect
              label={_("Users")}
              multiple
              onChangeValue={this.tt.onUserFilterChange}
              placeholder={_("All users")}
              testID="workstatusIndexScreen/userFilter"
              values={userIds.map(String)}
            />
          </View>
          <View
            style={styles.filterSelectProject ||= {
              flex: 1,
              minWidth: 220
            }}
            testID="workstatusIndexScreen/projectFilterField"
          >
            <ProjectsSelect
              label={_("Projects")}
              multiple
              onChangeValue={this.tt.onProjectFilterChange}
              placeholder={_("All projects")}
              testID="workstatusIndexScreen/projectFilter"
              values={projectIds.map(String)}
            />
          </View>
        </View>
      </Card>
    )
  }

  /**
   * Renders aggregate summary totals.
   * @returns {ReactNode} Summary card or null when unavailable.
   */
  renderSummary() {
    const {summary} = this.s
    if (!summary) return null
    const totalTimeSeconds = summary.totalTimeSeconds
    const totalTransportSeconds = summary.totalTimeTransportSeconds
    const totalTransportLength = summary.totalTransportLength
    const currency = summary.currency
    const themeName = this.tt.themeName
    const colors = this.tt.colors
    return (
      <Card
        style={styles.summaryCardSpacing ||= {marginBottom: 16}}
        testID="workstatusIndexScreen/summary"
      >
        <Text
          style={styles[`summaryTitle-${themeName}`] ||= {
            fontSize: 18,
            fontWeight: 700,
            marginBottom: 12
          }}
          testID="workstatusIndexScreen/summary/title"
        >
          {_("Overview")}
        </Text>
        <View
          style={styles.summaryRow ||= {
            flexDirection: "row",
            flexWrap: "wrap",
            gap: 16
          }}
          testID="workstatusIndexScreen/summary/row"
        >
          <View
            style={styles.summaryItem ||= {
              flex: 1,
              minWidth: 120
            }}
            testID="workstatusIndexScreen/summary/hours"
          >
            <Text
              style={styles[`summaryLabel-${themeName}`] ||= {
                color: colors.textMuted,
                fontSize: 13
              }}
              testID="workstatusIndexScreen/summary/hours/label"
            >
              {_("Hours")}
            </Text>
            <Text
              style={styles[`summaryValue-${themeName}`] ||= {
                fontSize: 20,
                fontWeight: 700
              }}
              testID="workstatusIndexScreen/summary/hours/value"
            >
              {formatHours(totalTimeSeconds)}
            </Text>
          </View>
          <View
            style={styles.summaryItem ||= {
              flex: 1,
              minWidth: 120
            }}
            testID="workstatusIndexScreen/summary/transport"
          >
            <Text
              style={styles[`summaryLabel-${themeName}`] ||= {
                color: colors.textMuted,
                fontSize: 13
              }}
              testID="workstatusIndexScreen/summary/transport/label"
            >
              {_("Transport")}
            </Text>
            <Text
              style={styles[`summaryValue-${themeName}`] ||= {
                fontSize: 20,
                fontWeight: 700
              }}
              testID="workstatusIndexScreen/summary/transport/value"
            >
              {formatHours(totalTransportSeconds)}
            </Text>
          </View>
          <View
            style={styles.summaryItem ||= {
              flex: 1,
              minWidth: 120
            }}
            testID="workstatusIndexScreen/summary/distance"
          >
            <Text
              style={styles[`summaryLabel-${themeName}`] ||= {
                color: colors.textMuted,
                fontSize: 13
              }}
              testID="workstatusIndexScreen/summary/distance/label"
            >
              {_("Transport length")}
            </Text>
            <Text
              style={styles[`summaryValue-${themeName}`] ||= {
                fontSize: 20,
                fontWeight: 700
              }}
              testID="workstatusIndexScreen/summary/distance/value"
            >
              {totalTransportLength > 0
                ? `${totalTransportLength.toFixed(0)} km`
                : "0"}
            </Text>
          </View>
          <View
            style={styles.summaryItem ||= {
              flex: 1,
              minWidth: 120
            }}
            testID="workstatusIndexScreen/summary/count"
          >
            <Text
              style={styles[`summaryLabel-${themeName}`] ||= {
                color: colors.textMuted,
                fontSize: 13
              }}
              testID="workstatusIndexScreen/summary/count/label"
            >
              {_("Entries")}
            </Text>
            <Text
              style={styles[`summaryValue-${themeName}`] ||= {
                fontSize: 20,
                fontWeight: 700
              }}
              testID="workstatusIndexScreen/summary/count/value"
            >
              {String(summary.totalCount)}
            </Text>
          </View>
          <View
            style={styles.summaryItemEarned ||= {
              flex: 1,
              minWidth: 120
            }}
            testID="workstatusIndexScreen/summary/earned"
          >
            <Text
              style={styles[`summaryLabelEarned-${themeName}`] ||= {
                color: colors.textMuted,
                fontSize: 13
              }}
              testID="workstatusIndexScreen/summary/earned/label"
            >
              {_("Earned")}
            </Text>
            <Text
              style={styles[`summaryValueEarned-${themeName}`] ||= {
                fontSize: 20,
                fontWeight: 700
              }}
              testID="workstatusIndexScreen/summary/earned/value"
            >
              {formatMoneyAmount(summary.earnedAmount, currency)}
            </Text>
          </View>
          <View
            style={styles.summaryItemTransportEarned ||= {
              flex: 1,
              minWidth: 120
            }}
            testID="workstatusIndexScreen/summary/transportEarned"
          >
            <Text
              style={styles[`summaryLabelTransportEarned-${themeName}`] ||= {
                color: colors.textMuted,
                fontSize: 13
              }}
              testID="workstatusIndexScreen/summary/transportEarned/label"
            >
              {_("Transport earned")}
            </Text>
            <Text
              style={styles[`summaryValueTransportEarned-${themeName}`] ||= {
                fontSize: 20,
                fontWeight: 700
              }}
              testID="workstatusIndexScreen/summary/transportEarned/value"
            >
              {formatMoneyAmount(summary.earnedTransportAmount, currency)}
            </Text>
          </View>
          <View
            style={styles.summaryItemTotalEarned ||= {
              flex: 1,
              minWidth: 120
            }}
            testID="workstatusIndexScreen/summary/totalEarned"
          >
            <Text
              style={styles[`summaryLabelTotalEarned-${themeName}`] ||= {
                color: colors.textMuted,
                fontSize: 13
              }}
              testID="workstatusIndexScreen/summary/totalEarned/label"
            >
              {_("Total earned")}
            </Text>
            <Text
              style={styles[`summaryValueTotalEarned-${themeName}`] ||= {
                fontSize: 20,
                fontWeight: 700
              }}
              testID="workstatusIndexScreen/summary/totalEarned/value"
            >
              {formatMoneyAmount(summary.totalEarnedAmount, currency)}
            </Text>
          </View>
          <View
            style={styles.summaryItemAfterTax ||= {
              flex: 1,
              minWidth: 120
            }}
            testID="workstatusIndexScreen/summary/afterTax"
          >
            <Text
              style={styles[`summaryLabelAfterTax-${themeName}`] ||= {
                color: colors.textMuted,
                fontSize: 13
              }}
              testID="workstatusIndexScreen/summary/afterTax/label"
            >
              {_("After tax (40%)")}
            </Text>
            <Text
              style={styles[`summaryValueAfterTax-${themeName}`] ||= {
                fontSize: 20,
                fontWeight: 700
              }}
              testID="workstatusIndexScreen/summary/afterTax/value"
            >
              {formatMoneyAmount(summary.afterTax40Amount, currency)}
            </Text>
          </View>
        </View>
      </Card>
    )
  }

  /**
   * Renders daily work status rows.
   * @returns {ReactNode} Daily rows or empty text.
   */
  renderDays() {
    const {dateFrom, dateTo, summary} = this.s
    if (!summary) return null
    const allDates = enumerateDates(dateFrom, dateTo)
    if (allDates.length === 0) {
      return (
        <Text
          style={styles[`emptyText-${this.tt.themeName}`] ||= {
            color: this.tt.colors.textMuted
          }}
          testID="workstatusIndexScreen/emptyText"
        >
          {_("No timelogs found")}
        </Text>
      )
    }
    /** @type {Map<string, WorkstatusDaySummary>} */
    const daysByDate = new Map()
    for (const day of summary.days) daysByDate.set(day.date, day)
    const days = allDates.map((date) => daysByDate.get(date) || {
      count: 0,
      date,
      earnedAmount: 0,
      earnedTransportAmount: 0,
      timeSeconds: 0,
      timeTransportSeconds: 0,
      transportLength: 0
    })
    const themeName = this.tt.themeName
    const colors = this.tt.colors
    const currency = summary.currency
    const todayDate = todayIsoDate()
    return (
      <Card testID="workstatusIndexScreen/days">
        <ScrollView
          horizontal
          testID="workstatusIndexScreen/days/scroll"
        >
          <View
            style={styles.daysTable ||= {
              minWidth: 790
            }}
            testID="workstatusIndexScreen/days/table"
          >
            <View
              style={styles[`daysHeader-${themeName}`] ||= {
                borderBottomColor: colors.border,
                borderBottomWidth: 1,
                flexDirection: "row",
                paddingBottom: 8
              }}
              testID="workstatusIndexScreen/days/header"
            >
              <Text
                style={styles[`dayHeaderDate-${themeName}`] ||= {
                  color: colors.textMuted,
                  fontSize: 13,
                  fontWeight: 700,
                  width: 140
                }}
                testID="workstatusIndexScreen/days/header/date"
              >
                {_("Date")}
              </Text>
              <Text
                style={styles[`dayHeaderHours-${themeName}`] ||= {
                  color: colors.textMuted,
                  fontSize: 13,
                  fontWeight: 700,
                  textAlign: "right",
                  width: 90
                }}
                testID="workstatusIndexScreen/days/header/hours"
              >
                {_("Hours")}
              </Text>
              <Text
                style={styles[`dayHeaderTransport-${themeName}`] ||= {
                  color: colors.textMuted,
                  fontSize: 13,
                  fontWeight: 700,
                  textAlign: "right",
                  width: 100
                }}
                testID="workstatusIndexScreen/days/header/transport"
              >
                {_("Transport")}
              </Text>
              <Text
                style={styles[`dayHeaderEarned-${themeName}`] ||= {
                  color: colors.textMuted,
                  fontSize: 13,
                  fontWeight: 700,
                  textAlign: "right",
                  width: 120
                }}
                testID="workstatusIndexScreen/days/header/earned"
              >
                {_("Earned")}
              </Text>
              <Text
                style={styles[`dayHeaderTransportEarned-${themeName}`] ||= {
                  color: colors.textMuted,
                  fontSize: 13,
                  fontWeight: 700,
                  textAlign: "right",
                  width: 150
                }}
                testID="workstatusIndexScreen/days/header/transportEarned"
              >
                {_("Transport earned")}
              </Text>
              <Text
                style={styles[`dayHeaderDistance-${themeName}`] ||= {
                  color: colors.textMuted,
                  fontSize: 13,
                  fontWeight: 700,
                  textAlign: "right",
                  width: 100
                }}
                testID="workstatusIndexScreen/days/header/distance"
              >
                {_("Distance")}
              </Text>
              <Text
                style={styles[`dayHeaderEntries-${themeName}`] ||= {
                  color: colors.textMuted,
                  fontSize: 13,
                  fontWeight: 700,
                  textAlign: "right",
                  width: 90
                }}
                testID="workstatusIndexScreen/days/header/entries"
              >
                {_("Entries")}
              </Text>
            </View>
            {days.map((day) => {
              const isToday = day.date === todayDate
              let rowBackgroundColor = "transparent"
              let rowBorderColor = colors.divider
              if (isToday) {
                rowBackgroundColor = colors.infoBackground
                rowBorderColor = colors.info
              }
              return (
                <View
                  key={day.date}
                  style={styles[`dayRow-${themeName}-${isToday}`] ||= {
                    backgroundColor: rowBackgroundColor,
                    borderBottomColor: rowBorderColor,
                    borderBottomWidth: 1,
                    flexDirection: "row",
                    paddingVertical: 10
                  }}
                  testID={`workstatusIndexScreen/day-${day.date}`}
                >
                  <Text
                    style={styles[`dayCellDate-${themeName}`] ||= {
                      width: 140
                    }}
                    testID={`workstatusIndexScreen/day-${day.date}/date`}
                  >
                    {formatDateLocal(day.date)}
                  </Text>
                  <Text
                    style={styles[`dayCellHours-${themeName}`] ||= {
                      textAlign: "right",
                      width: 90
                    }}
                    testID={`workstatusIndexScreen/day-${day.date}/hours`}
                  >
                    {formatHours(day.timeSeconds)}
                  </Text>
                  <Text
                    style={styles[`dayCellTransport-${themeName}`] ||= {
                      textAlign: "right",
                      width: 100
                    }}
                    testID={`workstatusIndexScreen/day-${day.date}/transport`}
                  >
                    {formatHours(day.timeTransportSeconds)}
                  </Text>
                  <Text
                    style={styles[`dayCellEarned-${themeName}`] ||= {
                      textAlign: "right",
                      width: 120
                    }}
                    testID={`workstatusIndexScreen/day-${day.date}/earned`}
                  >
                    {formatMoneyAmount(day.earnedAmount, currency)}
                  </Text>
                  <Text
                    style={styles[`dayCellTransportEarned-${themeName}`] ||= {
                      textAlign: "right",
                      width: 150
                    }}
                    testID={`workstatusIndexScreen/day-${day.date}/transportEarned`}
                  >
                    {formatMoneyAmount(day.earnedTransportAmount, currency)}
                  </Text>
                  <Text
                    style={styles[`dayCellDistance-${themeName}`] ||= {
                      color: colors.textMuted,
                      textAlign: "right",
                      width: 100
                    }}
                    testID={`workstatusIndexScreen/day-${day.date}/distance`}
                  >
                    {day.transportLength > 0
                      ? `${day.transportLength.toFixed(0)} km`
                      : ""}
                  </Text>
                  <Text
                    style={styles[`dayCellEntries-${themeName}`] ||= {
                      color: colors.textMuted,
                      textAlign: "right",
                      width: 90
                    }}
                    testID={`workstatusIndexScreen/day-${day.date}/entries`}
                  >
                    {String(day.count)}
                  </Text>
                </View>
              )
            })}
          </View>
        </ScrollView>
      </Card>
    )
  }

  /**
   * Applies the start date filter.
   * @param {string} value Date value entered by the user.
   * @returns {void} Nothing.
   */
  onDateFromChange = (value) => {
    if (this.dateFromDebounceTimer) clearTimeout(this.dateFromDebounceTimer)
    this.dateFromDebounceTimer = setTimeout(() => {
      pushQueryParamUpdates(this.tt.router, this.tt.currentQueryParams(), "/workstatus", {
        date_from: value || undefined
      })
      this.dateFromDebounceTimer = null
    }, 300)
    this.s.dateFrom = value
    this.s.summary = null
  }

  /**
   * Applies the end date filter.
   * @param {string} value Date value entered by the user.
   * @returns {void} Nothing.
   */
  onDateToChange = (value) => {
    if (this.dateToDebounceTimer) clearTimeout(this.dateToDebounceTimer)
    this.dateToDebounceTimer = setTimeout(() => {
      pushQueryParamUpdates(this.tt.router, this.tt.currentQueryParams(), "/workstatus", {
        date_to: value || undefined
      })
      this.dateToDebounceTimer = null
    }, 300)
    this.s.dateTo = value
    this.s.summary = null
  }

  /**
   * Applies selected user filters.
   * @param {(string | number)[] | undefined} values Selected user values.
   * @returns {void} Nothing.
   */
  onUserFilterChange = (values) => {
    const nextIds = identifierListParam(values, "User IDs")
    if (sameIds(nextIds, this.s.userIds)) return
    pushQueryParamUpdates(this.tt.router, this.tt.currentQueryParams(), "/workstatus", {
      user_ids: nextIds.length === 0 ? undefined : nextIds.map(String)
    })
    this.s.summary = null
    this.s.userIds = nextIds
  }

  /**
   * Applies selected project filters.
   * @param {(string | number)[] | undefined} values Selected project values.
   * @returns {void} Nothing.
   */
  onProjectFilterChange = (values) => {
    const nextIds = identifierListParam(values, "Project IDs")
    if (sameIds(nextIds, this.s.projectIds)) return
    pushQueryParamUpdates(this.tt.router, this.tt.currentQueryParams(), "/workstatus", {
      project_ids: nextIds.length === 0 ? undefined : nextIds.map(String)
    })
    this.s.projectIds = nextIds
    this.s.summary = null
  }

  /**
   * Loads the work status summary for the active filters.
   * @returns {Promise<void>} Resolves after summary data is loaded.
   * @rejects {Error} When summary loading fails.
   */
  loadData = async () => {
    const {dateFrom, dateTo, projectIds, userIds} = this.s
    const requestId = ++this.loadRequestId
    this.s.loading = true
    this.s.status = null
    try {
      const summary = await Timelog.workstatusSummary({
        dateFrom,
        dateTo,
        projectIds: projectIds.map(String),
        userIds: userIds.map(String)
      })
      if (this.loadRequestId !== requestId) return
      this.s.loading = false
      this.s.summary = summary
    } catch (error) {
      if (this.loadRequestId !== requestId) return
      this.s.loading = false
      this.s.status = {
        kind: "error",
        text: errorMessage(error)
      }
      throw error
    }
  }
}

/** @type {NamedExoticComponent<WorkstatusIndexScreenProps>} */
const WorkstatusIndexScreenComponent = memo(shapeComponent(WorkstatusIndexScreen))

export default WorkstatusIndexScreenComponent

/**
 * Compares two sorted identifier lists.
 * @param {string[]} a First identifier list.
 * @param {string[]} b Second identifier list.
 * @returns {boolean} True when both lists contain the same ids in order.
 */
function sameIds(a, b) {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false
  }
  return true
}
